/**
 * How a paying subscriber moves between plans — pure rules, no environment.
 *
 * Polar allows one subscription per customer, so a plan change is a product
 * change on that subscription, and `proration_behavior` decides when it lands.
 * These rules pick it: anything that costs the customer more starts now and is
 * charged the prorated difference now (`invoice`); anything that costs less
 * waits for the end of the period they already paid for (`next_period`), so
 * nobody loses features mid-period and no credit is issued.
 *
 * Annual → monthly is always scheduled, even onto a higher tier. Applied now it
 * would restart the billing period and credit back months of prepaid annual
 * time — the customer wanting the higher tier today picks its annual product.
 *
 * Imported by the web app to label plan cards, so the button a customer clicks
 * and the change the server makes can never disagree. Keep it free of anything
 * that needs a Worker environment.
 */

import type { PlanCode } from "../schemas/billing";
import { planRank } from "./plan-limits";

export type BillingInterval = "month" | "year";

export type PlanPosition = {
  interval: BillingInterval;
  planCode: PlanCode;
};

export type ImmediatePlanChangeKind = "switch_to_annual" | "upgrade";

export type ScheduledPlanChangeKind =
  | "downgrade"
  | "switch_to_monthly"
  | "upgrade_at_renewal";

export type PlanChange =
  | { kind: "same" }
  | {
      kind: ImmediatePlanChangeKind;
      proration: "invoice";
      timing: "now";
    }
  | {
      kind: ScheduledPlanChangeKind;
      proration: "next_period";
      timing: "period_end";
    };

/** A change that actually moves the subscription somewhere. */
export type PlanChangeDecision = Exclude<PlanChange, { kind: "same" }>;

export type PlanChangeKind = PlanChangeDecision["kind"];

export type PlanChangeTiming = PlanChangeDecision["timing"];

function immediate(kind: ImmediatePlanChangeKind): PlanChange {
  return { kind, proration: "invoice", timing: "now" };
}

function scheduled(kind: ScheduledPlanChangeKind): PlanChange {
  return { kind, proration: "next_period", timing: "period_end" };
}

export function classifyPlanChange(
  from: PlanPosition,
  to: PlanPosition,
): PlanChange {
  const rankDelta = planRank(to.planCode) - planRank(from.planCode);

  if (from.interval === "year" && to.interval === "month") {
    if (rankDelta > 0) {
      return scheduled("upgrade_at_renewal");
    }

    return scheduled(rankDelta === 0 ? "switch_to_monthly" : "downgrade");
  }

  if (rankDelta < 0) {
    return scheduled("downgrade");
  }

  if (rankDelta > 0) {
    return immediate("upgrade");
  }

  return from.interval === to.interval
    ? { kind: "same" }
    : immediate("switch_to_annual");
}

export type TrialEffect =
  | { endsAt: Date; kind: "continues" }
  | { kind: "ends_now" };

const dayMs = 24 * 60 * 60 * 1000;

/**
 * What an immediate plan change does to a running trial. Polar carries the
 * trial over, recomputing its end from the NEW product's trial length; when
 * that product has no trial, or the recomputed end has already passed, the
 * trial ends on the spot and the new plan is billed immediately.
 *
 * `trialDays` is the target product's trial length — the catalog reads it as
 * days, which is the only trial interval our products use.
 */
export function trialEffectOfImmediateChange({
  now,
  trialDays,
  trialStart,
}: {
  now: Date;
  trialDays: number;
  trialStart: Date;
}): TrialEffect {
  if (trialDays <= 0) {
    return { kind: "ends_now" };
  }

  const endsAt = new Date(trialStart.getTime() + trialDays * dayMs);

  return endsAt.getTime() > now.getTime()
    ? { endsAt, kind: "continues" }
    : { kind: "ends_now" };
}
